import React, { Fragment, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { usePopperTooltip } from 'react-popper-tooltip';
import tooltipStyles from 'react-popper-tooltip/dist/styles.css';
import styles from './styles.css';
import cn from 'classnames';

// Create root level element for react-tooltips
const domNode = document.createElement('div');
document.body.appendChild(domNode);

/**
 * Tooltip component that renders on the left side of wrapped component
 */
const Tooltip = ({
  children,
  position,
  disable,
  renderInBody,
  delayShow,
  containerClassName,
  onButtonPress,
  isShowTip,
  containerTagElement,
  disableFocus,
  toolTipContainerClassName,
  tooltipArrowClassName,
  isContainerInlineBlock,
  tooltipProps = {},
  ...otherProps
}) => {
  const { target, description } = otherProps;
  const [isVisible, setIsVisible] = useState(false);
  const tooltipRef = useRef(null);
  const config = {
    placement: position,
    delayShow,
    trigger: ['focus', 'hover'],
  };
  if (disable) {
    config.trigger = null;
  }
  if (onButtonPress) {
    config.trigger = ['click'];
  }

  config.onVisibleChange = (v) => {
    setIsVisible(v);
    v && otherProps.onTooltipVisible?.(tooltipRef.current);
  };

  const { getArrowProps, getTooltipProps, setTooltipRef, setTriggerRef } =
    usePopperTooltip(config);

  const wrappedElement = React.cloneElement(children, {
    ...otherProps,
    'data-tip': true,
    'data-for': target,
  });

  const onFocus = () => {
    if (disable) return;
    setIsVisible(true);
  };

  const onBlur = () => {
    if (disable) return;
    setIsVisible(false);
  };

  const onClick = () => {
    if (onButtonPress && !disable) {
      setIsVisible(true);
    }
  };

  const tooltip = (
    <div
      {...tooltipProps}
      ref={(ref) => {
        setTooltipRef(ref);
        tooltipRef.current = ref;
      }}
      {...getTooltipProps({
        className: `${tooltipStyles['tooltip-container']} ${styles.container} ${toolTipContainerClassName} py-0.8 px-4.2 rounded-lg text-12 text-white leading-4 bg-gray-800 border-b-2 z-50`,
      })}
    >
      <div
        {...getArrowProps({
          className: `${tooltipStyles['tooltip-arrow']} ${styles.arrow} ${tooltipArrowClassName}`,
        })}
      />
      {description}
    </div>
  );

  const container = React.createElement(
    containerTagElement,
    {
      className: cn(containerClassName, {
        'inline-block': isContainerInlineBlock,
      }),
      ref: setTriggerRef,
      onFocusCapture: disableFocus ? () => {} : onFocus,
      onBlurCapture: onBlur,
      onClick,
    },
    wrappedElement,
  );

  return (
    <Fragment>
      {container}
      {isVisible || isShowTip
        ? renderInBody
          ? ReactDOM.createPortal(tooltip, domNode)
          : tooltip
        : null}
    </Fragment>
  );
};

Tooltip.propTypes = {
  target: PropTypes.string,
  description: PropTypes.string.isRequired,
  position: PropTypes.string,
  containerClassName: PropTypes.string,
  containerTagElement: PropTypes.string,
  toolTipContainerClassName: PropTypes.string,
  tooltipArrowClassName: PropTypes.string,
  isContainerInlineBlock: PropTypes.bool,
};

Tooltip.defaultProps = {
  position: 'top',
  containerClassName: '',
  containerTagElement: 'div',
  toolTipContainerClassName: '',
  tooltipArrowClassName: '',
  isContainerInlineBlock: true,
};

export default Tooltip;
